"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { buildNewsMediaUrl } from "@/utils/newsMediaUrl";

interface NewsImageProps {
  src?: string | null;
  alt: string;
  fill?: boolean;
  width?: number;
  height?: number;
  className?: string;
  sizes?: string;
  priority?: boolean;
}

const LOGO_PATH = "/sdtc.webp";

export default function NewsImage({
  src,
  alt,
  fill,
  width,
  height,
  className,
  sizes,
  priority,
}: NewsImageProps) {
  const [imgSrc, setImgSrc] = useState(src || LOGO_PATH);
  const [hasError, setHasError] = useState(false);

  // Reset error state when src changes and process URL
  useEffect(() => {
    if (src) {
      const processedUrl = buildNewsMediaUrl(src, LOGO_PATH);
      setImgSrc(processedUrl);
      setHasError(false);
    } else {
      setImgSrc(LOGO_PATH);
    }
  }, [src]);

  // If error occurred or no src, use regular img tag with onError
  if (hasError || !src) {
    return (
      <img
        src={LOGO_PATH}
        alt={alt}
        className={className}
        style={fill ? { width: "100%", height: "100%", objectFit: "cover" } : { width, height }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = LOGO_PATH;
        }}
      />
    );
  }

  // Try to use Next.js Image, but handle errors by falling back to img tag
  // Note: onError may not fire reliably for 404s in production, so we also check onLoadingComplete
  return (
    <div style={fill ? { position: "relative", width: "100%", height: "100%" } : undefined}>
      <Image
        src={imgSrc}
        alt={alt}
        fill={fill}
        width={fill ? undefined : width || 300}
        height={fill ? undefined : height || 180}
        className={className}
        sizes={fill ? sizes || "(max-width: 768px) 100%, 33vw" : undefined}
        priority={priority}
        unoptimized={imgSrc?.startsWith('http://localhost:7001') || 
                     imgSrc?.startsWith('http://127.0.0.1:7001') ||
                     imgSrc?.startsWith('http://192.168') ||
                     imgSrc?.startsWith('http://10.') ||
                     imgSrc?.startsWith('http://172.')}
        onError={() => {
          // Fallback to regular img tag if Next.js Image fails
          setHasError(true);
          setImgSrc(LOGO_PATH);
        }}
        onLoadingComplete={(result) => {
          // Double-check: if image loaded but has 0 dimensions, it's likely broken
          if (result.naturalWidth === 0 || result.naturalHeight === 0) {
            setHasError(true);
            setImgSrc(LOGO_PATH);
          }
        }}
      />
    </div>
  );
}

